import { useState } from "react";
import type { MapNodeSettings, MapRootName } from "@fortex/api";
import { Field, Input, Segmented, Switch, TextArea } from "@fortex/map";
import type { AppLabels } from "../../i18n";
import type { AdminKind } from "./treeModel";

export type SettingsPatch = Partial<MapNodeSettings>;

export type KindSettingsProps = {
  kind: AdminKind;
  root: MapRootName;
  settings: MapNodeSettings;
  labels: AppLabels;
  onChange: (patch: SettingsPatch) => void;
};

export const previewCenter = { lat: 46.3585, lon: 17.7935, zoom: 13 };

export function tileCoords(lat: number, lon: number, zoom: number): { x: number; y: number; z: number } {
  const scale = 2 ** zoom;
  const x = Math.floor(((lon + 180) / 360) * scale);
  const rad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * scale);

  return { x, y, z: zoom };
}

export function tilePreviewUrl(template: string | null | undefined, subdomains: string | null | undefined): string | null {
  if (!template || !template.includes("{z}")) return null;

  const { x, y, z } = tileCoords(previewCenter.lat, previewCenter.lon, previewCenter.zoom);
  const sub = (subdomains ?? "abc").trim();

  return template
    .replace("{s}", sub.length > 0 ? sub.charAt(0) : "a")
    .replace("{z}", String(z))
    .replace("{x}", String(x))
    .replace("{y}", String(y))
    .replace("{r}", "");
}

export function numberOrNull(value: string): number | null {
  if (value.trim().length === 0) return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

export function optionsText(options: Record<string, string> | null | undefined): string {
  return Object.entries(options ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
}

export function parseOptionsText(text: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const line of text.split(/\r?\n/)) {
    const index = line.indexOf("=");

    if (index <= 0) continue;

    const key = line.substring(0, index).trim();

    if (key.length > 0) result[key] = line.substring(index + 1).trim();
  }

  return result;
}

function NumberField({ label, value, onChange, min, max }: { label: string; value: number | null | undefined; onChange: (value: number | null) => void; min?: number; max?: number }) {
  return (
    <Field label={label}>
      <Input type="number" inputMode="numeric" min={min} max={max} value={value === null || value === undefined ? "" : String(value)} onChange={event => onChange(numberOrNull(event.target.value))} />
    </Field>
  );
}

function ZoomRange({ settings, labels, onChange }: Pick<KindSettingsProps, "settings" | "labels" | "onChange">) {
  return (
    <div className="fxapp-form-row">
      <NumberField label={labels.minZoom} value={settings.minZoom} min={0} max={24} onChange={minZoom => onChange({ minZoom })} />
      <NumberField label={labels.maxZoom} value={settings.maxZoom} min={0} max={24} onChange={maxZoom => onChange({ maxZoom })} />
    </div>
  );
}

function TileSettings({ settings, labels, onChange }: KindSettingsProps) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const preview = tilePreviewUrl(settings.urlTemplate, settings.subdomains);

  return (
    <>
      <Field label={labels.urlTemplate} required>
        <Input mono value={settings.urlTemplate ?? ""} placeholder="https://{s}.tile.example/{z}/{x}/{y}.png" onChange={event => onChange({ urlTemplate: event.target.value })} />
      </Field>
      <div className="fxapp-preview" data-fxmap="tile-preview">
        {preview !== null && !previewFailed ? (
          <img className="fxapp-preview-tile" src={preview} alt={labels.previewTile} width={128} height={128} onError={() => setPreviewFailed(true)} onLoad={() => setPreviewFailed(false)} />
        ) : (
          <div className="fxapp-preview-tile" role="img" aria-label={labels.previewTile} />
        )}
        <span className="fxmap-muted fxmap-mono">{preview ?? ""}</span>
        {previewFailed ? <span className="fxmap-field-error">{labels.previewFailed}</span> : null}
      </div>
      <div className="fxapp-form-row">
        <Field label={labels.subdomains}>
          <Input value={settings.subdomains ?? ""} onChange={event => onChange({ subdomains: event.target.value })} />
        </Field>
        <Field label={labels.attribution}>
          <Input value={settings.attribution ?? ""} onChange={event => onChange({ attribution: event.target.value })} />
        </Field>
      </div>
      <ZoomRange settings={settings} labels={labels} onChange={onChange} />
      <div className="fxapp-form-row">
        <NumberField label={labels.maxNativeZoom} value={settings.maxNativeZoom} min={0} max={24} onChange={maxNativeZoom => onChange({ maxNativeZoom })} />
        <NumberField label={labels.tileSize} value={settings.tileSize} min={64} max={1024} onChange={tileSize => onChange({ tileSize })} />
      </div>
      <Switch checked={settings.detectRetina === true} onChange={detectRetina => onChange({ detectRetina })} label={labels.detectRetina} showLabel />
      <Switch checked={settings.dark === true} onChange={dark => onChange({ dark })} label={labels.dark} showLabel />
    </>
  );
}

function SpecialSettings({ settings, labels, onChange }: KindSettingsProps) {
  const options = settings.providerOptions ?? {};
  const { key, ...rest } = options;

  return (
    <>
      <Field label={labels.provider}>
        <Segmented<"google" | "bing">
          label={labels.provider}
          value={settings.provider === "bing" ? "bing" : "google"}
          options={[
            { value: "google", label: "Google" },
            { value: "bing", label: "Bing" }
          ]}
          onChange={provider => onChange({ provider })}
        />
      </Field>
      <Field label={labels.providerKey}>
        <Input mono value={key ?? ""} onChange={event => onChange({ providerOptions: { ...rest, key: event.target.value } })} />
      </Field>
      <Field label={labels.providerOptions}>
        <TextArea mono rows={4} defaultValue={optionsText(rest)} onBlur={event => onChange({ providerOptions: { ...parseOptionsText(event.target.value), ...(key ? { key } : {}) } })} />
      </Field>
      <Field label={labels.attribution}>
        <Input value={settings.attribution ?? ""} onChange={event => onChange({ attribution: event.target.value })} />
      </Field>
      <Switch checked={settings.dark === true} onChange={dark => onChange({ dark })} label={labels.dark} showLabel />
    </>
  );
}

function FolderSettings({ settings, root, labels, onChange }: KindSettingsProps) {
  return (
    <>
      <Field label={labels.folder} required>
        <Input mono value={settings.folder ?? ""} onChange={event => onChange({ folder: event.target.value || null })} />
      </Field>
      <Field label={labels.sldLayer}>
        <Input mono value={settings.layer ?? ""} placeholder={labels.anySld} onChange={event => onChange({ layer: event.target.value || null })} />
      </Field>
      <Field label={labels.searchLabel} hint={labels.searchLabelHint}>
        <Input value={settings.searchLabel ?? ""} maxLength={500} onChange={event => onChange({ searchLabel: event.target.value || null })} />
      </Field>
      <ZoomRange settings={settings} labels={labels} onChange={onChange} />
      <Switch checked={settings.defaultOn === true} onChange={defaultOn => onChange({ defaultOn })} label={labels.defaultOn} showLabel />
      {root === "MAP" ? <Switch checked={settings.noCache === true} onChange={noCache => onChange({ noCache })} label={labels.noCache} showLabel /> : null}
    </>
  );
}

function GroupSettings({ settings, labels, onChange }: KindSettingsProps) {
  return (
    <>
      <Field label={labels.selectionMode}>
        <Segmented<"single" | "multi">
          label={labels.selectionMode}
          value={settings.selection === "single" ? "single" : "multi"}
          options={[
            { value: "multi", label: labels.selectionMulti },
            { value: "single", label: labels.selectionSingle }
          ]}
          onChange={selection => onChange({ selection })}
        />
      </Field>
      <Switch checked={settings.collapsed === true} onChange={collapsed => onChange({ collapsed })} label={labels.collapsed} showLabel />
    </>
  );
}

function GeoObjectSettings({ settings, root, labels, onChange }: KindSettingsProps) {
  return (
    <>
      <Switch checked={settings.includeDescendants === true} onChange={includeDescendants => onChange({ includeDescendants })} label={labels.includeDescendants} showLabel />
      <Switch checked={settings.cluster !== false} onChange={cluster => onChange({ cluster })} label={labels.cluster} showLabel />
      <Switch checked={settings.labels === true} onChange={labelsOn => onChange({ labels: labelsOn })} label={labels.labels} showLabel />
      <Switch checked={settings.defaultOn === true} onChange={defaultOn => onChange({ defaultOn })} label={labels.defaultOn} showLabel />
      <ZoomRange settings={settings} labels={labels} onChange={onChange} />
      {root === "LAYER" ? (
        <>
          <Switch checked={settings.simplify !== false} onChange={simplify => onChange({ simplify })} label={labels.simplify} showLabel />
          <NumberField label={labels.maxFeatures} value={settings.maxFeatures} min={1} onChange={maxFeatures => onChange({ maxFeatures })} />
        </>
      ) : null}
    </>
  );
}

export function KindSettings(props: KindSettingsProps) {
  switch (props.kind) {
    case "Group":
      return <GroupSettings {...props} />;
    case "Tile":
      return <TileSettings {...props} />;
    case "Special":
      return <SpecialSettings {...props} />;
    case "Folder":
      return <FolderSettings {...props} />;
    case "GeoObject":
      return <GeoObjectSettings {...props} />;
  }
}
